import { useState, useEffect, useMemo } from "react";
import { BarChart3, Users, Building2, UserCircle, LogOut, Bell, ShoppingBag, Clipboard, CalendarDays, MoonStar, Sun, PanelLeftClose, PanelLeftOpen, UserCheck } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import "./styles.css";
import { useUser } from "@/utils/userContext/UserContext";
import { useLogout } from "@/utils/auth/Logout";
import { usePermissions } from "@/utils/auth/usePermissions";
import cowonderMiniLogo from "@/assets/cowonderMinilogoPurple.png";

const allNavItems = [
  {
    title: "Dashboard",
    href: "/dashboard",
    icon: BarChart3,
  },
  {
    title:'Produtos',
    href:"/dashboard/produtos",
    icon:ShoppingBag,
  },
  {
    title: "Clientes",
    href: "/dashboard/clientes",
    icon: Users,
  },
  {
    title: "Inscrições",
    href: "/dashboard/inscricoes",
    icon: Clipboard,
  },
  {
    title: "Afiliados",
    href: "/dashboard/afiliados",
    icon: UserCheck,
  },
  {
    title: "Agenda",
    href: "/dashboard/agenda",
    icon: CalendarDays,
  },
  {
    title:"Notificações",
    href:"/dashboard/notificacoes",
    icon: Bell,
  },
  {
    title: "Minha Empresa",
    href: "/dashboard/minha-empresa",
    icon: Building2,
  },
];

type SidebarProps = {
  theme?: "light" | "dark";
  onToggleTheme?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
};

export const Sidebar = ({ theme = "light", onToggleTheme, collapsed = false, onToggleCollapse }: SidebarProps) => {
  const logout = useLogout();
  const location = useLocation();
  const currentPath = location.pathname;
  const { userData } = useUser();
  const { canAccess } = usePermissions();

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  // Filtra os itens da navegação com base nas permissões do usuário
  const navItems = useMemo(() => {
    return allNavItems.filter((item) => canAccess(item.href));
  }, [canAccess]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileViewport(mobile);
      if (!mobile) setIsMobileOpen(false);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => {
    if (!isMobileViewport) return;
    setIsMobileOpen((prev) => !prev);
  };

  const closeMobileSidebar = () => {
    if (!isMobileViewport) return;
    setIsMobileOpen(false);
  };

  const isSidebarOpen = isMobileViewport ? isMobileOpen : !collapsed;

  return (
  <div className="sidebar-container">
    {isMobileViewport && isSidebarOpen && (
      <button
        type="button"
        className="sidebar-backdrop"
        aria-label="Fechar menu lateral"
        onClick={closeMobileSidebar}
      />
    )}
    <div className="sidebar-header sidebar-header-top cursor-pointer" onClick={toggleSidebar}>
      <div className="logo-icon">
        <img
          src={cowonderMiniLogo}
          alt="Logo Cowonder"
          className="w-[40px] h-auto sm:w-[30px] hover:opacity-80 transition-opacity"
        />
      </div>
    </div>

    <aside className={`sidebar ${isSidebarOpen ? "expanded" : "collapsed"}`}>
      <div className="sidebar-header" onClick={toggleSidebar}>
        <div className="logo-icon">
          <img
            src={cowonderMiniLogo}
            alt="Logo Cowonder"
            className="w-[40px] h-auto sm:w-[30px] hover:opacity-80 transition-opacity"
          />
        </div>
        <span className={`logo-text ${isSidebarOpen ? "visible" : "hidden"}`}>Cowonder</span>
        {!isMobileViewport && (
          <button
            type="button"
            className="collapse-toggle-btn"
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse?.();
            }}
            aria-label={collapsed ? "Expandir sidebar" : "Encolher sidebar"}
            title={collapsed ? "Expandir sidebar" : "Encolher sidebar"}
          >
            {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
          </button>
        )}
      </div>

      {isSidebarOpen && (
        <>
          <nav className="sidebar-nav">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`sidebar-item ${currentPath === item.href ? "active" : ""}`}
                onClick={closeMobileSidebar}
              >
                <item.icon className="sidebar-item-icon" />
                <span className="sidebar-item-text">{item.title}</span>
              </Link>
            ))}
          </nav>

          <div className="sidebar-footer">
            <button
              type="button"
              className="theme-toggle-btn"
              onClick={onToggleTheme}
              aria-label={theme === "dark" ? "Ativar modo claro" : "Ativar modo escuro"}
              title={theme === "dark" ? "Ativar modo claro" : "Ativar modo escuro"}
            >
              {theme === "dark" ? <Sun className="theme-toggle-icon" /> : <MoonStar className="theme-toggle-icon" />}
              <span>{theme === "dark" ? "Modo claro" : "Modo escuro"}</span>
            </button>
            <div className="user-profile">
              <div className="user-avatar">{userData?.name?.charAt(0)}</div>
              <div className="user-info">
                <span className="user-name">{userData?.name}</span>
              </div>
            </div>
            <div className="user-menu">
              <Link to="/dashboard/meu-perfil" className="user-menu-item">
                <UserCircle className="user-menu-icon" />
                <span>Perfil</span>
              </Link>
              <button className="user-menu-item" onClick={() => logout()}>
                <LogOut className="user-menu-icon" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </>
      )}
    </aside>
  </div>
  );
};
